import { ActionDef, ACTION_BY_ID } from '@/features/org/utils/action-catalogue/actionCatalogue';

/**
 * Scope selection per action — what the UI keeps in state and what
 * round-trips through `policies.actions[]`.
 *
 * `kind: 'all'`    → ARN(s) with `*` in the relevant axes; matches every
 *                    current AND future resource at that level.
 * `kind: 'specific'` → app-axis: a list of appIds. For app+env-axis,
 *                    additionally a per-app set of envIds (or 'all' to
 *                    mean every env under that app, current + future).
 */
export type ScopeSelection = { kind: 'all' } | { kind: 'specific'; apps: SpecificApp[] };

export interface SpecificApp {
  appId: string;
  /** Only meaningful for app+env-axis actions. 'all' means every env. */
  envs: 'all' | string[];
}

const ARN_PREFIX = 'arn:tile:';

/** Parse `<service>:<verb>` out of a 2-segment scope id. */
function splitScope(id: string): { service: string; verb: string } {
  const [service, verb] = id.split(':');
  return { service: service || '*', verb: verb || '*' };
}

/**
 * Expand a (action, selection) pair into one or more ARNs.
 *
 *   action='live_layer:publishProd', kind='all', orgId='org_X'
 *     →  ['arn:tile:org_X:*::live_layer:*:publishProd']
 *
 *   action='live_layer:publishProd', kind='specific', apps=[
 *     {appId:'app_A', envs:'all'},
 *     {appId:'app_B', envs:['env_1','env_2']},
 *   ]
 *     →  ['arn:tile:org_X:app_A::live_layer:*:publishProd',
 *         'arn:tile:org_X:app_B::live_layer:env_1:publishProd',
 *         'arn:tile:org_X:app_B::live_layer:env_2:publishProd']
 */
export function expandToArns(
  action: ActionDef,
  selection: ScopeSelection,
  orgId: string,
): string[] {
  const { service, verb } = splitScope(action.id);

  // Org-only actions are always orgId-only, axis segments stay `*`.
  if (action.dimension === 'org' || selection.kind === 'all') {
    return [`${ARN_PREFIX}${orgId}:*::${service}:*:${verb}`];
  }

  // 'specific' on an app- or app+env-axis action.
  const out: string[] = [];
  for (const a of selection.apps) {
    if (action.dimension === 'app') {
      out.push(`${ARN_PREFIX}${orgId}:${a.appId}::${service}:*:${verb}`);
      continue;
    }
    // app+env dimension
    if (a.envs === 'all') {
      out.push(`${ARN_PREFIX}${orgId}:${a.appId}::${service}:*:${verb}`);
    } else {
      for (const envId of a.envs) {
        out.push(`${ARN_PREFIX}${orgId}:${a.appId}::${service}:${envId}:${verb}`);
      }
    }
  }
  // De-dup in case the caller passed the same app twice.
  return Array.from(new Set(out));
}

/**
 * Reverse: given the ARNs stored on a policy, group them by action id
 * and reconstruct the scope selection per action.
 *
 *   ['arn:tile:org_X:app_A::live_layer:env_1:publishProd',
 *    'arn:tile:org_X:app_A::live_layer:env_2:publishProd',
 *    'arn:tile:org_X:*::apps:*:read']
 *
 *     → Map([
 *         ['live_layer:publishProd', { kind:'specific', apps:[
 *           { appId:'app_A', envs:['env_1','env_2'] }
 *         ]}],
 *         ['apps:read', { kind:'all' }],
 *       ])
 *
 * Unknown actions (not in the catalogue) are dropped silently.
 */
export function parseArns(arns: string[]): Map<string, ScopeSelection> {
  type AccApp = { appId: string; envSet: Set<string>; envWild: boolean };
  type Acc = { allWild: boolean; appsById: Map<string, AccApp> };
  const buckets = new Map<string, Acc>();

  for (const raw of arns) {
    const segs = raw.split(':');
    // arn:tile:<orgId>:<appId>::<service>:<resourceId>:<verb>  →  8 segs
    if (segs.length !== 8 || segs[0] !== 'arn' || segs[1] !== 'tile') continue;
    const appId = segs[3];
    const service = segs[5];
    const resourceId = segs[6];
    const verb = segs[7];
    const actionId = `${service}:${verb}`;
    const action = ACTION_BY_ID.get(actionId);
    if (!action) continue;

    let acc = buckets.get(actionId);
    if (!acc) {
      acc = { allWild: false, appsById: new Map() };
      buckets.set(actionId, acc);
    }

    if (action.dimension === 'org') {
      acc.allWild = true; // org-axis always wild — no per-resource state to track.
      continue;
    }
    if (appId === '*') {
      acc.allWild = true; // wildcard app means "all apps + future" regardless of envs.
      continue;
    }
    // App-specific ARN
    let app = acc.appsById.get(appId);
    if (!app) {
      app = { appId, envSet: new Set(), envWild: false };
      acc.appsById.set(appId, app);
    }
    if (action.dimension === 'app+env') {
      if (resourceId === '*') app.envWild = true;
      else app.envSet.add(resourceId);
    } else {
      // app-axis: resourceId is irrelevant; presence of this row means "this app".
      app.envWild = true;
    }
  }

  const out = new Map<string, ScopeSelection>();
  for (const [actionId, acc] of buckets) {
    if (acc.allWild) {
      out.set(actionId, { kind: 'all' });
      continue;
    }
    const apps: SpecificApp[] = [];
    for (const app of acc.appsById.values()) {
      apps.push({
        appId: app.appId,
        envs: app.envWild ? 'all' : Array.from(app.envSet),
      });
    }
    out.set(actionId, { kind: 'specific', apps });
  }
  return out;
}

/** Short, human-friendly summary of a scope selection for the chip UI. */
export function describeScope(
  action: ActionDef,
  selection: ScopeSelection,
  appNameById?: Map<string, string>,
): string {
  if (selection.kind === 'all') {
    if (action.dimension === 'org') return 'Org-wide';
    if (action.dimension === 'app') return 'All apps · current + future';
    return 'All apps · all envs · current + future';
  }
  const appCount = selection.apps.length;
  if (action.dimension === 'app') {
    if (appCount === 0) return 'Nothing selected';
    if (appCount === 1) {
      const a = selection.apps[0];
      return appNameById?.get(a.appId) || a.appId;
    }
    return `${appCount} apps`;
  }
  // app+env
  let envTotal = 0;
  let envWildApps = 0;
  for (const a of selection.apps) {
    if (a.envs === 'all') envWildApps += 1;
    else envTotal += a.envs.length;
  }
  const appPart =
    appCount === 1
      ? appNameById?.get(selection.apps[0].appId) || selection.apps[0].appId
      : `${appCount} apps`;
  const envPart =
    envWildApps === appCount
      ? 'all envs'
      : envWildApps > 0
      ? `${envTotal} envs + ${envWildApps} all`
      : `${envTotal} env${envTotal === 1 ? '' : 's'}`;
  return `${appPart} · ${envPart}`;
}
